import Button from '@digitalshared/button';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApiService } from '../../js/ApiService';
import { getOrGeneratePayload } from '../../js/utils';
import STATUSLogo from '../../styles/themes/status/assets/STATUS_logo_black.svg';
import useClient from '../util/useClient/useClient';
import useCooldown from '../util/useCooldown/useCooldown';
import useEnvSite from '../util/useEnvSite/useEnvSite';
const LinkExpired = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const email = query.get('email') || '';
  // Expired POS registration link: the new link is requested with the expired token, not the email
  const isRegistration = query.get('type') === 'registration';
  const registrationToken = location.state?.registrationToken || '';
  const canResend = isRegistration ? !!registrationToken : !!email;
  const client = useClient();
  const history = useHistory();
  const getEnv = useEnvSite();

  const [isLoading, setIsLoading] = useState(false);
  const { cooldown, startCooldown } = useCooldown(0);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (cooldown === 0) {
      setErrorMsg('');
    }
  }, [cooldown]);

  const handleRegistrationResend = async () => {
    try {
      setIsLoading(true);
      setErrorMsg('');
      const response = await ApiService.resendMagicRegistrationLink(registrationToken);

      if (response.status === 200) {
        history.push('/checkYourInbox', {
          email: email,
          isRegistrationLink: true,
          registrationToken: registrationToken,
          initialCooldown: 30
        });
      } else if (response.status === 429) {
        setErrorMsg('Too many link requests. Please try again later.');
        startCooldown(30);
      } else {
        setErrorMsg('Failed to send a new link. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting new registration link', error);
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (isLoading || cooldown > 0) return;

    if (isRegistration) {
      await handleRegistrationResend();
      return;
    }

    if (!email) {
      setErrorMsg('Email address is missing. Please try logging in again.');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMsg('');
      const extraPayload = await getOrGeneratePayload(client);
      const formData = new FormData();
      formData.append('email', email);
      formData.append('context', 'WEB');
      formData.append('client_id', client);

      if (extraPayload) {
        Object.entries(extraPayload).forEach(([key, val]) => {
          formData.append(key, val);
        });
      }

      const response = await ApiService.requestMagicLink(formData);

      if (response.status === 200) {
        const payloadObj = {
          email: email,
          context: 'WEB',
          client_id: client,
          ...extraPayload
        };
        history.push('/checkYourInbox', {
          email: email,
          isMagicLink: true,
          initialCooldown: 30,
          magicLinkPayload: payloadObj
        });
      } else if (response.status === 429) {
        setErrorMsg('Too many magic link requests. Please try again later.');
        startCooldown(30);
      } else {
        setErrorMsg('Failed to send magic link. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting magic link', error);
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="body bg-white" data-testid="link-expired-page" style={{ top: 0 }}>
      <div className="w-100 py-4 mb-0" style={{ borderBottom: '1px solid #E5E5E5' }}>
        <div className="d-flex w-100 justify-content-center">
          <img
            src={STATUSLogo}
            alt="STATUS logo"
            data-testid="status-icon"
            style={{ height: '40px' }}
          />
        </div>
      </div>
      <div className="container pt-4 pb-5">
        <div className="mx-auto text-center" style={{ maxWidth: '420px' }}>
          <h1 className="h1 text-uppercase mb-4">Link Expired</h1>
          <div className="body2 mt-4 text-muted" data-testid="expired-instruction">
            {email ? (
              <>
                The link we sent to{' '}
                <strong className="text-dark font-weight-bold" data-testid="recipient-email">
                  {email}
                </strong>{' '}
                has expired.
              </>
            ) : (
              'The link we sent has expired.'
            )}{' '}
            {isRegistration
              ? 'Request a new link below to finish setting up your STATUS account.'
              : 'For your security, one-time links are valid for 15 minutes. Request a new one-time link below to instantly sign in to your STATUS Rewards account.'}
          </div>

          {errorMsg && (
            <div
              className="text-danger mt-3"
              data-testid="error-msg"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              {errorMsg}
            </div>
          )}

          <div className="mt-5 d-flex flex-column align-items-center" style={{ gap: '1rem' }}>
            {canResend ? (
              <Button
                category="primary"
                className="btn btn-primary btn-block"
                disabled={isLoading || cooldown > 0}
                loading={isLoading}
                onClick={handleResend}
                data-testid="resend-link-button"
              >
                {isLoading
                  ? 'Requesting...'
                  : cooldown > 0
                    ? `Resend Link (${cooldown}s)`
                    : isRegistration
                      ? 'Request New Link'
                      : 'Resend One-Time Link'}
              </Button>
            ) : (
              <Button
                category="primary"
                className="btn btn-primary btn-block"
                onClick={() =>
                  (window.location.href = `https://${getEnv.env}.${getEnv.site}.com/login`)
                }
                data-testid="go-to-signin-button"
              >
                Go to Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkExpired;
